import type { AnyRouter } from "@tanstack/react-router";

/**
 * How long the creator site's router keeps pages in memory, for the visitor <html data-viewer>
 * names. A visitor without an account keeps pages they have seen for 30 s and up to five minutes
 * in memory, so Back and repeat visits answer at once. Anyone signed in (a member, the owner, a
 * preview) keeps nothing: each page they open is requested afresh and dropped as they leave it,
 * so member content never stays in this tab's memory. A prepared website's document is every
 * visitor's ("published"), so it keeps nothing until its viewer read says who is visiting.
 */
export function pageMemory(viewer: string | undefined) {
  const anonymous = viewer === "anonymous";
  return {
    defaultPreloadStaleTime: anonymous ? 30_000 : 0,
    defaultStaleTime: anonymous ? 30_000 : 0,
    defaultGcTime: anonymous ? 5 * 60_000 : 0,
  };
}

/**
 * A prepared website learned who is visiting. A visitor without an account keeps pages from now
 * on, the one they arrived on included: its data is as old as this document, which the selector
 * found current, not as old as the publication that prepared it, possibly hours ago, which the
 * router would read as long expired and request again on Back. Signing in drops what was kept.
 */
export function rememberPages(router: AnyRouter, viewer: "anonymous" | "signed-in") {
  router.update({ ...router.options, ...pageMemory(viewer) });
  if (viewer === "signed-in") return router.clearCache();
  const arrived = Math.round(performance.timeOrigin);
  for (const match of router.state.matches)
    if (match.updatedAt < arrived) match.updatedAt = arrived;
}
